/** sessionStorage key for pending/paid vendor product during onboarding */
export function vendorProductStorageKey(contactId) {
  return `vendorOnboarding_product_${contactId}`;
}

/** Persists which screen to show after refresh (step 2 vs 3). */
export const VENDOR_ONBOARDING_STEP = {
  INFO: "info",
  PAYMENT: "payment",
};

/**
 * Merges into the vendor onboarding session blob without dropping productItemId / paid.
 */
export function mergeVendorProductSession(contactId, partial) {
  const key = vendorProductStorageKey(contactId);
  let prev = {};
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) prev = JSON.parse(raw);
  } catch {
    prev = {};
  }
  sessionStorage.setItem(key, JSON.stringify({ ...prev, ...partial }));
}

/** Vendor agreement signature from first onboarding; additional ads can skip re-signing. */
export function vendorAgreementSignatureOnFile(contact) {
  return Boolean(String(contact?.onboard_signature ?? "").trim());
}

/**
 * Vendor advertising plans — Payment step only. `value` is stored as `adType` and must match backend pricing.
 * `months` is stored as `vendorAdPackageMonths` for approval term and expiration.
 */
/**
 * Listing rows may have `vendorAdPackageMonths` set (default 6 on save) while `adType` stayed empty.
 * Use this after GET so the Payment step and checkout see a consistent plan.
 */
export function inferVendorAdTypeFromItem(item) {
  const raw = item?.adType != null ? String(item.adType).trim() : "";
  if (raw) return raw;
  const m = Number(item?.vendorAdPackageMonths);
  if (m === 12) return "VendorAd12";
  if (m === 6) return "VendorAd6";
  return "";
}

export const AD_PLAN_CARDS = [
  {
    value: "VendorAd6",
    months: 6,
    title: "Vendor Ad (6 months)",
    priceLine: "$300.00 — just $50.00 per month",
    description: "Six-month vendor advertising on the platform.",
  },
  {
    value: "VendorAd12",
    months: 12,
    title: "Vendor Ad (12 months)",
    priceLine: "$500.00 — just $41.66 per month",
    description: "Twelve-month vendor advertising on the platform.",
  },
];

export const VENDOR_AGREEMENT_SECTIONS = [
  {
    title: "1. Platform and Services",
    paragraphs: [
      "BrokerAgentBase.com is a proprietary, cloud-based real estate brokerage software platform owned and operated by In Depth Realty in the State of Utah.",
      "The platform is utilized by licensed real estate agents affiliated with In Depth Realty for CRM and lead generation, transaction management and document coordination, back office operations, accounting and compliance, marketing and client communication, and general business operations.",
      "The platform includes a vendor advertising marketplace for third-party service providers to promote services to platform users, including agents, buyers, and sellers.",
      "Approved vendor advertisements are displayed within the Vendor section and organized by service categories such as Cleaning, Moving, Contractors, and related services.",
      "Company grants Vendor a limited, revocable, non-exclusive, non-transferable license to display advertising content on the platform during the applicable term.",
    ],
  },
  {
    title: "2. Eligibility and Authority",
    bullets: [
      "Vendor has full legal authority to enter into this Agreement.",
      "Vendor operates a legitimate business in compliance with all applicable laws.",
      "Any individual accepting this Agreement has authority to bind the Vendor entity.",
    ],
  },
  {
    title: "3. Advertising Submission and Approval",
    bullets: [
      "All advertisements are subject to prior review and written or electronic approval by Company.",
      "Company may approve or reject any advertisement, require modification or removal, and reclassify or recategorize advertisements in its sole discretion.",
      "Company is under no obligation to provide a reason for rejection.",
      "Submission does not guarantee acceptance or placement.",
    ],
  },
  {
    title: "4. Term; Commencement; Expiration",
    bullets: [
      "The advertising term commences upon Company approval of the advertisement.",
      "The term continues for the duration of the purchased package.",
      "Time is not prorated for delays caused by Vendor.",
      "Failure to utilize the full term does not entitle Vendor to extension or refund.",
    ],
  },
  {
    title: "5. Renewal; Auto-Expiration",
    bullets: [
      "Company will issue renewal notices approximately 30 days, 14 days, and 3 days prior to expiration.",
      "Vendor is solely responsible for maintaining accurate contact information and monitoring expiration dates.",
      "If Vendor fails to renew before expiration, the advertisement automatically terminates and related content may be removed permanently without further notice.",
      "Company has no obligation to retain, recover, or restore expired content.",
    ],
  },
  {
    title: "6. Fees; Payment; No Refunds",
    bullets: [
      "All fees are due in advance of publication.",
      "Payments are final and non-refundable once an advertisement is approved.",
      "No refunds are issued for early termination by Vendor, lack of performance, leads, or engagement, or removal/suspension due to breach.",
    ],
  },
  {
    title: "7. Account Access and Security",
    bullets: [
      "Vendor receives dashboard access upon approval.",
      "Vendor must maintain confidentiality of login credentials and is responsible for all activity under its account.",
      "Vendor must promptly notify Company of unauthorized access.",
      "Company is not liable for losses resulting from unauthorized account use.",
    ],
  },
  {
    title: "8. Content; Representations; Compliance",
    bullets: [
      "Vendor content must be accurate, lawful, non-misleading, and compliant with applicable laws.",
      "Vendor must own or secure all required rights, licenses, and permissions.",
      "Vendor content must not infringe third-party rights.",
      "Prohibited content includes false/deceptive claims, infringing materials, defamatory, obscene, or unlawful content.",
    ],
  },
  {
    title: "9. Indemnification",
    paragraphs: [
      "Vendor shall indemnify, defend, and hold harmless BrokerAgentBase.com, In Depth Realty, and their owners, officers, agents, employees, and affiliates from all claims, losses, damages, liabilities, costs, and expenses, including reasonable attorneys' fees, arising from Vendor advertisements or services, Vendor breach, or violations of law or third-party rights.",
    ],
  },
  {
    title: "10. Disclaimer of Warranties",
    bullets: [
      "Platform and services are provided on an 'as is' and 'as available' basis.",
      "Company disclaims all express and implied warranties including merchantability, fitness for a particular purpose, and non-infringement.",
      "Company does not warrant uninterrupted or error-free operation or guaranteed leads, revenue, or business opportunities.",
    ],
  },
  {
    title: "11. Limitation of Liability",
    bullets: [
      "Company is not liable for indirect, incidental, consequential, or punitive damages.",
      "Company's total aggregate liability shall not exceed total fees paid by Vendor within the preceding twelve (12) months.",
    ],
  },
  {
    title: "12. Termination; Suspension",
    bullets: [
      "Company may suspend or terminate Vendor access or remove advertisements at any time, with or without notice, for breach, non-compliance, or unlawful/fraudulent/unethical conduct.",
    ],
  },
  {
    title: "13. Intellectual Property",
    bullets: [
      "Vendor retains ownership of its content.",
      "Vendor grants Company a worldwide, royalty-free license to use, display, reproduce, and distribute content solely to provide services.",
    ],
  },
  {
    title: "14. Modifications",
    bullets: [
      "Company may modify this Agreement at any time.",
      "Updated terms are effective upon posting; continued use constitutes acceptance.",
    ],
  },
  {
    title: "15. Governing Law; Venue",
    bullets: [
      "This Agreement is governed by laws of the State of Utah.",
      "Any disputes shall be resolved exclusively in state or federal courts located within Utah.",
    ],
  },
  {
    title: "16. Severability",
    bullets: [
      "If any provision is invalid or unenforceable, remaining provisions remain in full force and effect.",
    ],
  },
  {
    title: "17. Entire Agreement",
    bullets: [
      "This Agreement constitutes the entire agreement and supersedes all prior agreements, representations, or understandings.",
    ],
  },
];
